import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/constants';
import { buildAuthHeaders, isTokenExpired } from '../utils/auth';

interface User {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
}

interface Book {
  id: number;
  title: string;
  author: string;
  coverImage?: string;
}

interface Review {
  id: number;
  userId: number;
  bookId: number;
  rating: number;
  title?: string;
  content?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  User?: User;
  Book?: Book;
}

interface ReviewsData {
  reviews: Review[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  averageRating: number;
  totalReviews: number;
}

interface ReviewData {
  bookId: number;
  rating: number;
  title?: string;
  content?: string;
  isPublic?: boolean;
}

interface UserReviewCheck {
  hasReview: boolean;
  review: Review | null;
}

interface ReviewsContextType {
  reviews: ReviewsData;
  userReviews: Review[];
  loading: boolean;
  fetchBookReviews: (bookId: number, page?: number, limit?: number) => Promise<ReviewsData | null>;
  fetchUserReviews: () => Promise<Review[] | null>;
  addReview: (reviewData: ReviewData) => Promise<Review | false>;
  updateReview: (reviewId: number, reviewData: Partial<ReviewData>) => Promise<Review | false>;
  deleteReview: (reviewId: number) => Promise<boolean>;
  checkUserReview: (bookId: number) => Promise<UserReviewCheck | null>;
}

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined);

export const useReviews = (): ReviewsContextType => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }
  return context;
};

interface ReviewsProviderProps {
  children: ReactNode;
}

export const ReviewsProvider: React.FC<ReviewsProviderProps> = ({ children }) => {
  const [reviews, setReviews] = useState<ReviewsData>({
    reviews: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    averageRating: 0,
    totalReviews: 0
  });
  const [userReviews, setUserReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, token, logout } = useAuth();

  // Obține recenziile pentru o carte
  const fetchBookReviews = async (bookId: number, page = 1, limit = 10): Promise<ReviewsData | null> => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/reviews/book/${bookId}?page=${page}&limit=${limit}`
      );

      if (response.ok) {
        const data: ReviewsData = await response.json();
        setReviews(data);
        return data;
      } else {
        console.error('Eroare la obținerea recenziilor');
        return null;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Obține recenziile utilizatorului
  const fetchUserReviews = async (): Promise<Review[] | null> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return null;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/reviews/user`, {
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (response.ok) {
        const data: Review[] = await response.json();
        setUserReviews(data);
        return data;
      } else if (response.status === 401) {
        logout();
        return null;
      } else {
        console.error('Eroare la obținerea recenziilor utilizatorului');
        return null;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Adaugă o recenzie nouă
  const addReview = async (reviewData: ReviewData): Promise<Review | false> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/reviews`, {
        method: 'POST',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(reviewData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Actualizează lista de recenzii pentru carte
        setReviews(prev => ({
          ...prev,
          reviews: [data.review, ...(prev.reviews || [])],
          totalItems: (prev.totalItems || 0) + 1
        }));
        
        // Actualizează lista de recenzii ale utilizatorului
        setUserReviews(prev => [data.review, ...prev]);
        
        return data.review;
      } else if (response.status === 401) {
        logout();
        return false;
      } else {
        const error = await response.json();
        console.error('Eroare la adăugarea recenziei:', error.message);
        return false;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return false;
    }
  };

  // Actualizează o recenzie
  const updateReview = async (reviewId: number, reviewData: Partial<ReviewData>): Promise<Review | false> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/reviews/${reviewId}`, {
        method: 'PUT',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(reviewData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Actualizează lista de recenzii pentru carte
        setReviews(prev => ({
          ...prev,
          reviews: prev.reviews?.map(review => 
            review.id === reviewId ? data.review : review
          ) || []
        }));
        
        // Actualizează lista de recenzii ale utilizatorului
        setUserReviews(prev => 
          prev.map(review => review.id === reviewId ? data.review : review)
        );
        
        return data.review;
      } else if (response.status === 401) {
        logout();
        return false;
      } else {
        const error = await response.json();
        console.error('Eroare la actualizarea recenziei:', error.message);
        return false;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return false;
    }
  };

  // Șterge o recenzie
  const deleteReview = async (reviewId: number): Promise<boolean> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (response.ok) {
        // Elimină recenzia din lista de recenzii pentru carte
        setReviews(prev => ({
          ...prev,
          reviews: prev.reviews?.filter(review => review.id !== reviewId) || [],
          totalItems: Math.max((prev.totalItems || 1) - 1, 0)
        }));
        
        // Elimină recenzia din lista de recenzii ale utilizatorului
        setUserReviews(prev => prev.filter(review => review.id !== reviewId));
        
        return true;
      } else if (response.status === 401) {
        logout();
        return false;
      } else {
        const error = await response.json();
        console.error('Eroare la ștergerea recenziei:', error.message);
        return false;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return false;
    }
  };

  // Verifică dacă utilizatorul a dat o recenzie pentru o carte
  const checkUserReview = async (bookId: number): Promise<UserReviewCheck | null> => {
    if (!user || !token || isTokenExpired(token)) {
      if (token && isTokenExpired(token)) {
        logout();
      }
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/reviews/check/${bookId}`, {
        headers: buildAuthHeaders(token, {
          'Content-Type': 'application/json'
        })
      });

      if (response.ok) {
        const data: UserReviewCheck = await response.json();
        return data;
      } else if (response.status === 401) {
        logout();
        return null;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Eroare:', error);
      return null;
    }
  };

  const value: ReviewsContextType = {
    reviews,
    userReviews,
    loading,
    fetchBookReviews,
    fetchUserReviews,
    addReview,
    updateReview,
    deleteReview,
    checkUserReview
  };

  return (
    <ReviewsContext.Provider value={value}>
      {children}
    </ReviewsContext.Provider>
  );
}; 
